"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Block = void 0;
const pixi_js_1 = require("pixi.js");
const Global_1 = require("../Global");
const ui_1 = require("@pixi/ui");
const rxjs_1 = require("rxjs");
const PromiseChain_1 = require("../../../shared/PromiseChain");
const TextField_1 = require("../objects/TextField");
const gradients_1 = require("@pixi-essentials/gradients");
const settings_1 = require("../config/settings");
class Block extends pixi_js_1.Container {
    constructor(app) {
        super();
        this.app = app;
        this.subs = new rxjs_1.Subscription();
        this.sigs = [];
        this.tms = new Set;
        this.textures = new Map();
        this.chain = Promise.resolve();
    }
    // TODO: Turn into "ButtonContainer" from PixiUI
    makeButton(url, bounce = 0) {
        const texture = pixi_js_1.Texture.from(url);
        const button = new pixi_js_1.Sprite(texture);
        button.anchor.set(0.5);
        return this.withbounce(new ui_1.ButtonContainer(button), bounce);
    }
    asButton(bounce = 0) {
        return this.withbounce(new ui_1.ButtonContainer(this), bounce);
    }
    withbounce(b, bounce = 0) {
        if (bounce) {
            let last = null;
            this.sigs.push(b.onDown.connect(() => {
                if (!last) {
                    last = { x: b.scale.x, y: b.scale.y };
                }
                b.scale.set(b.scale.x - (0, Global_1.px)(bounce), b.scale.y - (0, Global_1.px)(bounce));
            }));
            this.sigs.push(b.onUp.connect(() => {
                b.scale = last;
                last = null;
            }));
        }
        return b;
    }
    makeLinearGradient(colors, width, height, vertical = false) {
        const total = colors.length;
        const stops = colors.map((color, i) => {
            return {
                color,
                offset: i / total
            };
        });
        const graphics = (new pixi_js_1.Graphics()).beginFill(0xffffff)
            .drawRect(0, 0, (0, Global_1.px)(width), (0, Global_1.px)(height)).endFill();
        const texture = this.app.renderer.generateTexture(graphics);
        return gradients_1.GradientFactory.createLinearGradient((this.app.renderer), texture, {
            x0: 0, y0: 0, x1: (vertical ? 0 : texture.width), y1: (vertical ? texture.height : 0), colorStops: stops
        });
    }
    makeBox(options = {}, to = null) {
        const opts = Object.assign({
            borderColor: 0x222222,
            backgroundColor: 0xffffff,
            width: 200,
            height: 32,
            radius: 3,
            border: 2,
        }, options);
        const g = ((to === null || to === void 0 ? void 0 : to.clear()) || new pixi_js_1.Graphics()).beginFill(opts.borderColor)
            .drawRoundedRect(0, 0, (0, Global_1.px)(opts.width), (0, Global_1.px)(opts.height), (0, Global_1.px)(opts.radius + opts.border));
        const w = (0, Global_1.px)(opts.width - (opts.border * 2));
        const h = (0, Global_1.px)(opts.height - (opts.border * 2));
        if (opts.backgroundColor instanceof Array) {
            if (to && this.textures.has(to)) {
                this.textures.get(to).destroy(true);
            }
            const texture = this.makeLinearGradient(opts.backgroundColor, w / (0, Global_1.px)(), h / (0, Global_1.px)(), opts.vertical || false);
            this.textures.set(g, texture);
            g.beginTextureFill({ texture });
        }
        else if (opts.backgroundColor instanceof pixi_js_1.Texture) {
            g.beginTextureFill({ texture: opts.backgroundColor });
        }
        else {
            g.beginFill(opts.backgroundColor);
        }
        return g.drawRoundedRect((0, Global_1.px)(opts.border), (0, Global_1.px)(opts.border), w, h, (0, Global_1.px)(opts.radius));
    }
    makeTextfield(options = {}) {
        const opts = Object.assign({
            fontSize: 24,
            textColor: '#222',
            placeholderColor: '#888',
            maxLength: 255,
            placeholder: null,
            value: null,
            padding: [0, 0, 0, 0],
            focusClear: false,
            wiggles: false
        }, options);
        const graphics = this.makeBox(options);
        const text = new pixi_js_1.TextStyle({
            fontFamily: (0, Global_1.font)(),
            fontSize: (0, Global_1.fontSize)(opts.fontSize),
            padding: (0, Global_1.px)(6),
            fill: (!opts.value && opts.placeholder) ? opts.placeholderColor : opts.textColor
        });
        const input = new TextField_1.TextField(this.app, {
            bg: graphics,
            textStyle: text,
            maxLength: opts.maxLength,
            align: 'center',
            placeholder: opts.placeholder,
            value: opts.value,
            padding: opts.padding,
            cleanOnFocus: opts.focusClear,
            wiggles: opts.wiggles
        });
        // Change the color if it's just our placeholder
        input.onChange.connect((value) => {
            input.children.filter((c) => (c instanceof pixi_js_1.Text)).forEach((t) => {
                if (opts.placeholder && !value) {
                    t.style.fill = opts.placeholderColor;
                }
                else {
                    t.style.fill = opts.textColor;
                }
            });
        });
        return input;
    }
    static textStyle(size = 32, localfont = true) {
        let ff = settings_1.Settings.font;
        let fs = (0, Global_1.px)(size);
        if (typeof localfont === 'object' && (localfont === null || localfont === void 0 ? void 0 : localfont.l)) {
            ff = (0, Global_1.font)(localfont.l);
            fs = (0, Global_1.fontSize)(size, localfont.l);
        }
        else if (localfont) {
            ff = (0, Global_1.font)();
            fs = (0, Global_1.fontSize)(size);
        }
        return new pixi_js_1.TextStyle({
            fontWeight: 'lighter',
            fontFamily: ff,
            fontSize: fs,
            padding: (0, Global_1.px)(6)
        });
    }
    promisedTimeline(tm) {
        if (!this.destroyed) {
            return (0, Global_1.promisedTimeline)(tm, this.tms);
        }
        else {
            // We are gone, don't run any timelines!
            return Promise.resolve(tm === null || tm === void 0 ? void 0 : tm.kill());
        }
    }
    series(func) {
        // Keep the promise chain in our scope
        return (0, PromiseChain_1.series)(func, this);
    }
    layout() {
        this.children.forEach((child) => {
            if (typeof child.layout === 'function') {
                child.layout();
            }
        });
    }
    get isLandscapeBox() {
        const h = this.app.screen.height / (0, Global_1.px)();
        return this.isLandscape && h < Global_1.BreakPoint.Box;
    }
    get isLandscape() {
        return this.app.screen.width > this.app.screen.height;
    }
    get overSmallSize() {
        return (this.app.screen.height / (0, Global_1.px)()) >= Global_1.BreakPoint.Small;
    }
    get overMediumSize() {
        return (this.app.screen.height / (0, Global_1.px)()) >= Global_1.BreakPoint.Medium;
    }
    get overLargeSize() {
        return (this.app.screen.height / (0, Global_1.px)()) >= Global_1.BreakPoint.Large;
    }
    get center() {
        return { x: this.app.screen.width / 2, y: this.app.screen.height / 2 };
    }
    // Just to get Typescript to stop wining :(
    setTransform(x, y, scaleX, scaleY, rotation, skewX, skewY, pivotX, pivotY) {
        return super.setTransform(x, y, scaleX, scaleY, rotation, skewX, skewY, pivotX, pivotY);
    }
    destroy(_options) {
        var _a;
        this.textures.forEach((t) => {
            if (!t.destroyed) {
                t.destroy(true);
            }
        });
        this.textures.clear();
        this.tms.forEach((tm) => {
            tm === null || tm === void 0 ? void 0 : tm.kill();
        });
        this.tms.clear();
        (_a = this.subs) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        this.sigs.forEach((s) => {
            s === null || s === void 0 ? void 0 : s.disconnect();
        });
        // Important! Override Pixi default behavior and destroy children too (if we did not specify)
        if (!_options) {
            _options = {
                children: true,
                baseTexture: false
            };
        }
        super.destroy(_options);
    }
}
exports.Block = Block;
